import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/session';
import { LegalLinks } from '../components/LegalLinks';
import { initialOf, LangSwitch, t, tl } from '../i18n';
import { initials } from '../lib/format';
import { useWebPushState } from '../lib/webpush';
import { Bell } from './belege/Bell';
import { KanzleiContactsBox } from './chat/KanzleiContactsBox';
import { useChatUnread } from './chat/useChatUnread';
import { useClientApp } from './context';
import { useTurnOnPush } from './HomePush';
import type { PageInfo } from './page';

const NAV: { key: NonNullable<PageInfo['nav']>; label: string; to: string; icon?: string }[] = [
  { key: 'start', label: tl('Übersicht'), to: '/app' },
  { key: 'belege', label: tl('Finanzbuchhaltung'), to: '/app/belege' },
  { key: 'dokumente', label: tl('Dokumente'), to: '/app/dokumente' },
  { key: 'mitarbeiter', label: tl('Mitarbeiter'), to: '/app/mitarbeiter' },
  { key: 'jahresabschluss', label: tl('Jahresabschluss'), to: '/app/jahresabschluss' },
  { key: 'nachrichten', label: tl('Nachrichten'), to: '/app/nachrichten', icon: '✉' },
  { key: 'rechnungen', label: tl('Rechnungen'), to: '/app/rechnungen', icon: '€' },
];

// Wechsel ins Rechnungsmodul (Sam 13.10.: „Ein knopf. auf rechnungsmodul wechseln“): Rechnungen an die eigenen Kunden
// schreiben, unter /rechnung mit demselben Login. Dort führt „Zur Buchhaltung“ zurück.
function RechnungsmodulLink({ side }: { side?: boolean }) {
  return (
    <Link to="/rechnung" className="rm-switch">
      {side && (
        <span className="si" aria-hidden="true">
          🧾
        </span>
      )}
      {side ? t('Zum Rechnungsmodul') : `🧾 ${t('Rechnungsmodul')}`}
    </Link>
  );
}

// Rahmen der Mandanten-App: Kopfzeile (Logo oder Zurück, Titel, Glocke, Chat, Benutzer), Menü, Pfad, Seitenleiste am Desktop.
export function ClientLayout({ page, children }: { page: PageInfo; children: ReactNode }) {
  const navigate = useNavigate();
  const { me } = useClientApp();
  const chatUnread = useChatUnread();
  const onChat = page.nav === 'nachrichten';
  // Übersicht: Wortmarke statt Titel „Elbcons“ (sonst stünde der Name zweimal da)
  const home = page.title === 'Elbcons';

  return (
    <div className="mapp">
      <div className="app">
        <header className="bar">
          <div className="bar-top">
            {page.back ? (
              <button type="button" className="back" onClick={() => navigate(page.back!)}>
                <span className="chev">‹</span> {t('Zurück')}
              </button>
            ) : null}
            <Link to="/app" className={home ? 'logo wm' : 'logo'} aria-label={t('Zur Übersicht')} hidden={!!page.back} />
            <h1 className={home ? 'sr-only' : undefined}>{page.title}</h1>
            <Bell />
            <Link
              to="/app/nachrichten"
              className="chatbtn"
              aria-label={t('Chat mit Ihrer Kanzlei')}
              aria-current={onChat ? 'page' : undefined}
            >
              💬<span className="cbl"> {t('Chat')}</span>
              {chatUnread > 0 && !onChat && <span className="cb-n">{chatUnread}</span>}
            </Link>
            <UserMenu name={me.user.name} email={me.user.email} />
          </div>
          <nav className="topnav" aria-label={t('Menü')}>
            {NAV.map((n) => (
              <Link key={n.key} to={n.to} aria-current={page.nav === n.key ? 'page' : undefined}>
                {t(n.label)}
              </Link>
            ))}
            <RechnungsmodulLink />
          </nav>
          {page.crumbs && page.crumbs.length > 0 && (
            <nav className="crumbs" aria-label={t('Pfad')}>
              {page.crumbs.map((c, i) => (
                <span key={i} style={{ display: 'contents' }}>
                  {i > 0 && <span className="sep">›</span>}
                  {c.to ? (
                    <button type="button" onClick={() => navigate(c.to!)}>
                      {c.label}
                    </button>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
        </header>
        <nav className="side" aria-label={t('Bereiche')}>
          {NAV.map((n) => (
            <Link key={n.key} to={n.to} aria-current={page.nav === n.key ? 'page' : undefined}>
              <span className="si">{n.icon ?? initialOf(n.label)}</span>
              {t(n.label)}
              {n.key === 'nachrichten' && chatUnread > 0 && <span className="cnt">{chatUnread}</span>}
            </Link>
          ))}
          <RechnungsmodulLink side />
          <KanzleiContactsBox />
        </nav>
        <main id="screen">{children}</main>
      </div>
    </div>
  );
}

function UserMenu({ name, email }: { name: string; email: string }) {
  const { logout } = useSession();
  const { config } = useClientApp();
  const push = useWebPushState();
  const turnOnPush = useTurnOnPush();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div className="user" ref={ref}>
      <button type="button" className="user-btn" aria-expanded={open} aria-label={t('Konto')} onClick={() => setOpen((o) => !o)}>
        <span className="user-name">{name}</span>
        <i>{initials(name)}</i>
      </button>
      {open && (
        <div className="user-menu" role="menu">
          <b>{name}</b>
          <small>{email}</small>
          {config.loginCode && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/app/login-code');
              }}
            >
              {t('Login-Code per E-Mail')}
            </button>
          )}
          {push === 'ask' && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                turnOnPush();
              }}
            >
              {t('Push-Nachrichten einschalten')}
            </button>
          )}
          <LangSwitch className="langsw um" />
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            {t('Abmelden')}
          </button>
          <LegalLinks menu />
        </div>
      )}
    </div>
  );
}
